import { type Edge } from '@xyflow/react';

import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';

import {
  getInitialEdge,
  getStandardApprovalFlowNode,
  rootNode,
} from './initialNodes';
import { type CostCenter } from '../../budgets/models/costCenter';
import { type Team } from '../../budgets/models/team';
import {
  type Dimension,
  type ApprovalWorkflow,
  type ApprovalWorkflowNode,
} from '../approvalWorkflow';
import { type ApprovalSchemeWithMemberNames } from '../hooks/useApprovalSchemesQuery';
import { type CustomFieldValues } from '../hooks/useApprovalWorkflowCustomFieldValuesQuery';
import { type CustomNode } from '../node';
import { getSpendTypeTranslation } from '../spendType';

export const transformApprovalWorkflow = ({
  approvalWorkflow,
  t,
  designatedCustomFieldId,
  data,
}: {
  approvalWorkflow: ApprovalWorkflow;
  t: TGlobalFunctionTyped;
  designatedCustomFieldId: string;
  data: {
    costCenters: CostCenter[];
    teams: Team[];
    customFieldValues: CustomFieldValues;
    approvalSchemes: ApprovalSchemeWithMemberNames[];
  };
}) => {
  const transformedApprovalWorkflow = transformApprovalWorkflowToNodesAndEdges(
    approvalWorkflow,
    t,
  );
  const enrichedNodes = enrichNodesWithValueNames({
    nodes: transformedApprovalWorkflow.nodes,
    costCenters: data.costCenters,
    teams: data.teams,
    customValues: data.customFieldValues,
    t,
  });
  const nodesWithSchemes = attachApprovalSchemesToNodes({
    nodes: enrichedNodes,
    approvalSchemes: data.approvalSchemes,
  });
  const sortedNodes = sortNodes(
    nodesWithSchemes,
    transformedApprovalWorkflow.edges,
  );
  const nodesWithCustomFieldsConvertedToExpenseCategories =
    getNodesWithCustomFieldsConvertedToExpenseCategories(
      sortedNodes,
      designatedCustomFieldId,
    );
  return {
    nodes: nodesWithCustomFieldsConvertedToExpenseCategories,
    edges: transformedApprovalWorkflow.edges,
  };
};

const sortNodes = (nodes: CustomNode[], edges: Edge[]) => {
  const nodesByParent = new Map<string, CustomNode[]>();

  const workflowRootNode = nodes.find((node) => node.id === 'root');

  nodes.forEach((node) => {
    if (node.id === 'root') {
      return;
    } // Skip root node from grouping

    const parentEdge = edges.find((edge) => edge.target === node.id);
    const parentId = parentEdge?.source || 'root';

    if (!nodesByParent.has(parentId)) {
      nodesByParent.set(parentId, []);
    }
    const parentNodes = nodesByParent.get(parentId);
    if (parentNodes) {
      parentNodes.push(node);
    }
  });

  const sortedNodes: CustomNode[] = [];

  const sortNodesByValues = (nodeList: CustomNode[]): CustomNode[] => {
    return nodeList.sort((a, b) => {
      if (a.type !== 'baseNode' || b.type !== 'baseNode') {
        return 0;
      }

      const aValues = a.data.values;
      const bValues = b.data.values;

      // If 'all other' (null), put it at the beginning
      if (aValues === null && bValues !== null) {
        return -1;
      }
      if (aValues !== null && bValues === null) {
        return 1;
      }
      if (aValues === null && bValues === null) {
        return 0;
      }

      if (!aValues || !bValues) {
        return 0;
      }

      const aFirstValue = aValues[0]?.name || '';
      const bFirstValue = bValues[0]?.name || '';

      return aFirstValue.localeCompare(bFirstValue);
    });
  };

  nodesByParent.forEach((branchNodes) => {
    const sortedBranchNodes = sortNodesByValues(branchNodes);
    sortedNodes.push(...sortedBranchNodes);
  });

  if (workflowRootNode) {
    sortedNodes.unshift(workflowRootNode);
  }

  return sortedNodes;
};

export const enrichNodesWithValueNames = ({
  nodes,
  costCenters,
  teams,
  customValues,
  t,
}: {
  nodes: CustomNode[];
  costCenters: CostCenter[];
  teams: Team[];
  customValues: CustomFieldValues;
  t: TGlobalFunctionTyped;
}): CustomNode[] => {
  const costCentersMap = new Map(
    costCenters.map((costCenter) => [costCenter.id, costCenter.name]),
  );
  const teamsMap = new Map(teams.map((team) => [team.id, team.name]));
  const customValuesMap = new Map(Object.entries(customValues));

  const getValueName = (dimensionType: string, valueId: string): string => {
    switch (dimensionType) {
      case 'costCenter':
        return costCentersMap.get(valueId) || valueId;
      case 'team':
        return teamsMap.get(valueId) || valueId;
      case 'customField':
        return customValuesMap.get(valueId) || valueId;
      case 'spendType':
        return getSpendTypeTranslation(t, valueId);
      default:
        return valueId;
    }
  };

  const enrichNodeValues = (node: CustomNode): CustomNode => {
    if (node.type !== 'baseNode' || !node.data.dimension?.type) {
      return node;
    }

    const { dimension, values } = node.data;

    if (values === null || values === undefined) {
      return node;
    }

    const enrichedValues = values.map((value) => ({
      id: value.id,
      name: getValueName(dimension.type, value.id),
    }));

    return {
      ...node,
      data: { ...node.data, values: enrichedValues },
    };
  };

  return nodes.map(enrichNodeValues);
};

const attachApprovalSchemesToNodes = ({
  nodes,
  approvalSchemes,
}: {
  nodes: CustomNode[];
  approvalSchemes: ApprovalSchemeWithMemberNames[];
}): CustomNode[] => {
  if (approvalSchemes.length === 0) {
    return nodes;
  }

  const approvalSchemesMap = new Map(
    approvalSchemes.map((scheme) => [scheme.id, scheme]),
  );

  return nodes.map((node) => {
    if (node.type === 'baseNode' && node.data.schemeId) {
      const scheme = approvalSchemesMap.get(node.data.schemeId);
      if (scheme) {
        return {
          ...node,
          data: {
            ...node.data,
            approvalScheme: scheme,
          },
        };
      }
    }

    if (node.type === 'standardApprovalFlowNode' && node.data.schemeId) {
      const scheme = approvalSchemesMap.get(node.data.schemeId);
      if (scheme) {
        return {
          ...node,
          data: {
            ...node.data,
            approvalScheme: scheme,
          },
        };
      }
    }

    return node;
  });
};

const getNodesWithCustomFieldsConvertedToExpenseCategories = (
  nodes: CustomNode[],
  designatedCustomFieldId: string,
) => {
  return nodes.map((node) => {
    if (
      node.type === 'baseNode' &&
      node.data.dimension?.type === 'customField' &&
      node.data.dimension.customFieldId === designatedCustomFieldId
    ) {
      return {
        ...node,
        data: {
          ...node.data,
          dimension: { type: 'expenseCategory' },
        },
      };
    }
    return node;
  });
};

export const transformApprovalWorkflowToNodesAndEdges = (
  approvalWorkflow: ApprovalWorkflow,
  t: TGlobalFunctionTyped,
) => {
  const workflowRootNode = approvalWorkflow.definition;

  const standardApprovalFlowNode = getStandardApprovalFlowNode(
    approvalWorkflow.id,
    workflowRootNode.outcome.approvalSchemeId,
  );

  const { nodes, edges } = collectNodesAndEdges(
    workflowRootNode,
    rootNode.id,
    t,
  );

  return {
    nodes: [...nodes, standardApprovalFlowNode, rootNode],
    edges: [
      ...edges,
      getInitialEdge(rootNode.id, standardApprovalFlowNode.id, t),
    ],
  };
};

const collectNodesAndEdges = (
  parentWorkflowNode: ApprovalWorkflowNode,
  parentId: string | null = null,
  t: TGlobalFunctionTyped,
  pathPrefix: string = 'node',
) => {
  if (!parentWorkflowNode.branch) {
    return { nodes: [], edges: [] };
  }

  const collectedNodes: CustomNode[] = [];
  const collectedEdges: Edge[] = [];
  const { nodes: childNodes } = parentWorkflowNode.branch;
  const dimension = parentWorkflowNode.branch.dimension;

  childNodes.forEach((node, index) => {
    const newNodeId = `${pathPrefix}-${index}`;
    const newNode = nodeTemplate(
      newNodeId,
      node.outcome?.approvalSchemeId,
      dimension,
      node.values,
    );

    collectedNodes.push(newNode);
    if (parentId) {
      collectedEdges.push(edgeTemplate(parentId, newNodeId, t));
    }

    const { nodes: nestedChildNodes, edges: nestedChildEdges } =
      collectNodesAndEdges(node, newNodeId, t, newNodeId);
    collectedNodes.push(...nestedChildNodes);
    collectedEdges.push(...nestedChildEdges);
  });

  return { nodes: collectedNodes, edges: collectedEdges };
};

const nodeTemplate = (
  id: string,
  schemeId: string | undefined,
  dimension: Dimension,
  values: string[] | null,
): CustomNode => ({
  id,
  type: 'baseNode',
  data: {
    schemeId,
    dimension,
    values:
      values === null
        ? null
        : values.map((value) => ({ id: value, name: value })),
  },
  position: { x: 0, y: 0 },
});

const edgeTemplate = (
  sourceId: string,
  targetId: string,
  t: TGlobalFunctionTyped,
): Edge => ({
  id: `edge-${sourceId}-${targetId}`,
  source: sourceId,
  target: targetId,
  type: 'labelEdge',
  data: {
    labelText: t('approvalWorkflows.labelEdge.if'),
    labelVariant: 'purple',
  },
});
